import { Section, Sub, Card, Note, Fields, New, Shot } from '../../../components/docs/Blocks';

export default function Backups() {
  return (
    <Section
      id="backups"
      title="Backups"
      lead="A backup is a full copy of the world folder, kept outside it. That last part is the change from version 2, which stored backups inside the world — so deleting a world deleted its backups, exporting an .mcworld carried them along, and the world reported a size that was mostly its own history."
    >
      <Sub id="backups-automatic" title="Automatic backups">
        <Card>
          <p>
            By default, Toolchest takes a full backup before the first change you save
            in a session. Once per session rather than once per save: a backup taken
            before your third save already contains the first two, so a per-save policy
            would prune away the one state worth keeping — the world as it was when you
            started.
          </p>
          <p>
            Closing and reopening a world makes it eligible again. Moving between tabs
            does not.
          </p>
          <Fields
            items={[
              ['Always', 'The default. Backs up without asking.'],
              ['Ask each session', 'Offers a backup before the first save, with the option to save without one.'],
              ['Never', 'No automatic backups. Manual ones still work.'],
            ]}
          />
          <Note>
            If a backup fails, the save does not quietly happen anyway and you are not
            locked out either. Toolchest says what went wrong and offers to save
            without one.
          </Note>
        </Card>
      </Sub>

      <Sub id="backups-section" title="The Backups screen">
        <Shot
          name="backups"
          alt="The Backups screen listing worlds and the backups held for each"
        />
        <Card>
          <p>
            Backups is one of the screens that does not need a world open —
            deliberately, because a world you have deleted is exactly the one whose
            backups you want. Worlds on the left, that world’s backups in the middle,
            and the selected backup’s details on the right.
          </p>
          <Fields
            items={[
              ['Back up now', 'Takes a backup of the open world on demand, with a description. Manual backups are never removed by automatic cleanup.'],
              ['Restore', 'Replaces the world with the backup. A backup of the world as it is right now is taken first, so a restore can itself be undone.'],
              ['Verify', 'Reads every file in the backup and checks it against what was recorded when it was made.'],
              ['Lock', 'Protects a backup from automatic cleanup regardless of the retention limits.'],
              ['Delete', 'Removes a backup permanently.'],
            ]}
          />
          <p>
            Each backup records its size, how many files it holds, what it contains — a
            full world or level.dat only — where it came from, and what you were saving
            when it was taken.
          </p>
        </Card>
      </Sub>

      <Sub id="backups-remote" title={<>Servers and Realms <New>New in 3.1</New></>}>
        <Card>
          <p>
            Everything Toolchest changes on a server or Realm is backed up on this
            computer first. With no world open, the Backups page lists your servers
            and Realms under <strong>Servers &amp; Realms</strong>, below the
            worlds, each with how many backups it holds and their size.
          </p>
          <p>
            Each backup is named after what it was taken before — sending changes to
            a server, uploading a world, renaming a Realm, inviting a player — and
            its details say what it holds and what restoring it puts back.{' '}
            <strong>Restore</strong> puts it back: files and add-ons sent to a
            server, its server settings, a world uploaded over the server&rsquo;s,
            a Realm world replaced, uploaded or restored, and a Realm&rsquo;s
            settings. What it replaces is backed up too, so a restore can be undone.
          </p>
        </Card>
      </Sub>

      <Sub id="backups-restore" title="Restoring">
        <Card>
          <Fields
            items={[
              ['Over the open world', 'Toolchest closes the world first, restores, then opens it again and rebuilds every screen from what is now on disk. Unsaved changes are lost, and it says so before starting.'],
              ['A world that has been deleted', 'The world folder is recreated where it used to live. This is why backups are stored outside the world folder.'],
              ['Safety', 'The restore stages the files and swaps them in rather than deleting the world first, so an interrupted restore does not leave you with neither copy.'],
            ]}
          />
        </Card>
      </Sub>

      <Sub id="backups-retention" title="Retention">
        <Card>
          <p>
            Old backups are removed once <em>both</em> limits are exceeded — a count and
            a disk budget, because a count alone means nothing when one world is 200 KB
            and another is 2.4 GB. Both are in Settings.
          </p>
          <Fields
            items={[
              ['Backups to keep per world', 'Five by default.'],
              ['Disk space per world', 'Ten gigabytes by default.'],
              ['Never removed', 'Locked backups, backups you took by hand, and the newest one — always.'],
            ]}
          />
          <p>
            Settings also chooses <strong>where</strong> new backups are written. The
            default sits outside every world; you can point it at another drive
            instead. Backups already taken stay where they are.
          </p>
        </Card>
      </Sub>

      <Sub id="backups-v2" title={<>Moving version 2 backups out <New /></>}>
        <Card>
          <p>
            If you used version 2, its backups are still sitting inside your world
            folders. Toolchest finds them and offers, per world, to move them out to
            the new location.
          </p>
          <p>
            Doing so keeps them, frees the space inside the world folder, and stops
            them being deleted with the world or carried inside an .mcworld export. A
            world holding version 2 backups says so in the list rather than reporting
            zero backups.
          </p>
        </Card>
      </Sub>

      <Sub id="restore-points" title="Restore points">
        <Card>
          <p>
            Separate from backups, and not a history. A restore point is captured
            immediately before every save and covers the one thing neither staging nor
            backups do: a save that dies partway through, where each file is written
            atomically but the set is not.
          </p>
          <p>
            If that happens the save is rolled back automatically and Toolchest tells
            you the world was put back as it was. If the check afterwards finds
            something unexpected, it offers the rollback rather than performing it.
            Three are kept per world, and they go stale once Minecraft next compacts
            the world — so they are not a substitute for a backup.
          </p>
        </Card>
      </Sub>
    </Section>
  );
}
